import { defineStore } from 'pinia';
import { Core, ConnectionNode } from '@voximplant/websdk';
import {
  CallEvent,
  CallLoader,
  callToken,
  type Call,
} from '@voximplant/websdk/modules/call-manager';
import {
  AudioProcessingType,
  DeviceKind,
  StreamHelper,
  StreamLoader,
  streamToken,
  type DeviceTrackerHelper,
  type InputDevice,
  type LocalStream,
  type OutputDevice,
} from '@voximplant/websdk/modules/stream';
import {
  isCallIntentResponse,
  isRecord,
  isString,
  type AudioDevicePreferences,
} from '@calloneer/contracts';
import { apiRequest } from '../api.js';

type UiCallState = 'IDLE' | 'PREPARING' | 'DIALING' | 'RINGING' | 'CONNECTED' | 'ENDED' | 'FAILED';
const isOneTimeHash = (value: unknown): value is { readonly hash: string } =>
  isRecord(value) && isString(value.hash);

let sdkCall: Call | null = null;
let tracker: DeviceTrackerHelper | null = null;

export const useCallStore = defineStore('call', {
  state: (): {
    state: UiCallState;
    error: string | null;
    muted: boolean;
    startedAt: number | null;
    microphones: readonly InputDevice[];
    speakers: readonly OutputDevice[];
  } => ({
    state: 'IDLE',
    error: null,
    muted: false,
    startedAt: null,
    microphones: [],
    speakers: [],
  }),
  getters: {
    active: (state): boolean =>
      ['PREPARING', 'DIALING', 'RINGING', 'CONNECTED'].includes(state.state),
  },
  actions: {
    async refreshDevices(): Promise<void> {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        this.microphones = devices
          .filter((device) => device.kind === 'audioinput')
          .map(
            (device): InputDevice => ({
              id: device.deviceId,
              label: device.label,
              groupId: device.groupId,
              kind: DeviceKind.Microphone,
            }),
          );
        this.speakers = devices
          .filter((device) => device.kind === 'audiooutput')
          .map(
            (device): OutputDevice => ({
              id: device.deviceId,
              label: device.label,
              groupId: device.groupId,
              kind: DeviceKind.Speaker,
            }),
          );
      } catch {
        this.microphones = [];
        this.speakers = [];
      }
    },
    async start(input: {
      readonly destination: string;
      readonly callerIdId: string;
      readonly audio: AudioDevicePreferences;
    }): Promise<void> {
      if (this.active) throw new Error('A call is already active');
      this.state = 'PREPARING';
      this.error = null;
      try {
        const audioProcessingConstraints: MediaTrackConstraints = {
          echoCancellation: input.audio.echoCancellation,
          noiseSuppression: input.audio.noiseSuppression,
          autoGainControl: input.audio.autoGainControl,
        };
        const audioConstraints: MediaTrackConstraints = {
          ...audioProcessingConstraints,
          ...(input.audio.inputId ? { deviceId: { ideal: input.audio.inputId } } : {}),
        };
        await navigator.mediaDevices
          .getUserMedia({ audio: audioConstraints, video: false })
          .then((stream) => {
            stream.getTracks().forEach((track) => {
              track.stop();
            });
          });
        await this.refreshDevices();
        const intent = await apiRequest({
          path: '/v1/me/calls',
          method: 'POST',
          body: { destination: input.destination, callerIdId: input.callerIdId },
          guard: isCallIntentResponse,
        });
        const core = Core.init({});
        core.registerModules([StreamLoader(), CallLoader()]);
        const node = Object.values(ConnectionNode).find(
          (candidate) => String(candidate) === intent.voxNode,
        );
        if (!node) throw new Error('Voximplant connection node is unsupported');
        await core.client.connect({ node, autoReconnect: true });
        const loginKey = await core.client.requestOneTimeKey({ username: intent.voxUsername });
        const key = await apiRequest({
          path: '/v1/me/voximplant/one-time-key',
          method: 'POST',
          body: { loginKey },
          guard: isOneTimeHash,
        });
        await core.client.loginOneTimeKey({ username: intent.voxUsername, hash: key.hash });
        const manager = core.getModule(callToken);
        const stream = core.getModule(streamToken);
        if (!manager || !stream) throw new Error('Voximplant call modules did not initialize');
        tracker = stream.createHelper(StreamHelper.DeviceTracker);
        tracker.enableTracker();
        tracker.shouldSendVideo.value = false;
        tracker.audioProcessing.value = AudioProcessingType.Disabled;
        const applyAudioProcessingConstraints = (localStream: LocalStream | null): void => {
          if (!localStream) return;
          void localStream.track
            .applyConstraints(audioProcessingConstraints)
            .catch(() => undefined);
        };
        tracker.audioStream.watch(applyAudioProcessingConstraints);
        const microphone = tracker.microphones.value.find(
          (device) => device.id === input.audio.inputId,
        );
        const speaker = tracker.speakers.value.find((device) => device.id === input.audio.outputId);
        if (microphone) tracker.currentMicrophone.value = microphone;
        if (speaker) tracker.currentSpeaker.value = speaker;
        this.microphones = tracker.microphones.value;
        this.speakers = tracker.speakers.value;
        tracker.microphones.watch((devices) => {
          this.microphones = devices;
        });
        tracker.speakers.watch((devices) => {
          this.speakers = devices;
        });
        sdkCall = manager.createCall(intent.route, {
          customData: JSON.stringify({ callId: intent.callId, intentToken: intent.intentToken }),
          receiveVideo: false,
        });
        sdkCall.addEventListener(CallEvent.StartRinging, () => {
          this.state = 'RINGING';
        });
        sdkCall.addEventListener(CallEvent.Connected, () => {
          this.state = 'CONNECTED';
          this.startedAt = Date.now();
        });
        sdkCall.addEventListener(CallEvent.Failed, () => {
          this.state = 'FAILED';
          this.error = 'The call could not be connected';
          void this.cleanup();
        });
        sdkCall.addEventListener(CallEvent.Disconnected, () => {
          this.state = 'ENDED';
          void this.cleanup();
        });
        sdkCall.addEventListener(CallEvent.CallUpgrade, (event) => {
          event.payload.reject();
        });
        await tracker.attachCall(sdkCall);
        applyAudioProcessingConstraints(tracker.audioStream.value);
        this.state = 'DIALING';
        await sdkCall.start();
      } catch (error) {
        this.state = 'FAILED';
        this.error = error instanceof Error ? error.message : 'Call failed';
        await this.cleanup();
      }
    },
    toggleMute(): void {
      if (!sdkCall) return;
      this.muted = sdkCall.toggleMicrophone();
    },
    sendDtmf(tone: string): void {
      if (sdkCall && /^[0-9*#]$/.test(tone)) sdkCall.sendDTMF(tone);
    },
    hangup(): void {
      sdkCall?.hangup();
      this.state = 'ENDED';
      void this.cleanup();
    },
    selectMicrophone(id: string): void {
      const device = tracker?.microphones.value.find((item) => item.id === id);
      if (tracker && device) tracker.currentMicrophone.value = device;
    },
    selectSpeaker(id: string): void {
      const device = tracker?.speakers.value.find((item) => item.id === id);
      if (tracker && device) tracker.currentSpeaker.value = device;
    },
    async cleanup(): Promise<void> {
      const current = tracker;
      tracker = null;
      sdkCall = null;
      if (current) await current.clear().catch(() => undefined);
    },
    reset(): void {
      if (!this.active) {
        this.state = 'IDLE';
        this.error = null;
        this.startedAt = null;
        this.muted = false;
      }
    },
  },
});
